import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { type EmojiConfig, createEmojiConfig } from '@ale-rainbow/noto-emoji';
import { Service } from 'rainbow-web-sdk/services/service';
import * as notoEmoji from "@ale-rainbow/noto-emoji";
import { stringifyError } from '../utils/stringifyError';
export type { Emoji, EmojiCategories } from '@ale-rainbow/noto-emoji';

export const EMOJI_SVC = 'EmojiService';

export const UNICODE_EMOJI_REGEX =
    // all emojis which are not in a <span></span>
    // the inner regex is the same as the one in EMOJI_REGEX above
    new RegExp(`(?:(?!<span.*"\\k<emoji>"))(?<emoji>(${notoEmoji.EMOJI_REGEX.source}))(?!.*span>)`, 'gu');

export interface EmojiData {
    code: string;
    unicode: string;
    title: string;
}
export class EmojiService extends Service {
    private declare _config: EmojiConfig;

    private logger = LoggerService.getInstance();

    public static getInstance() {
        return Service.create(EMOJI_SVC, EmojiService);
    }

    public static build() {
        return new EmojiService();
    }

    public constructor() {
        super();
        createEmojiConfig({
            emojiJsonUrl: '/cacheV2/noto-emoji/emojilist.json',
            className: 'notoemoji',
            ascii: true,
            html: true,
        })
            .then((val) => {
                this._config = val;
            })
            .catch((error: unknown) => {
                this.logger.error(`Failed to fetch emojis list: ${stringifyError(error)}`);
            });
    }

    public get config(): EmojiConfig {
        return this._config;
    }

    public asciiToUnicode(text: string, config?: EmojiConfig): string {
        return notoEmoji.asciiToUnicode(config ?? this.config, text);
    }

    public shortnameToUnicode(text: string, config?: EmojiConfig): string {
        return notoEmoji.shortnameToUnicode(config ?? this.config, text);
    }

    public unicodeToShortname(text: string, config?: EmojiConfig): string {
        return notoEmoji.unicodeToShortname(config ?? this.config, text);
    }

    public getEmojiData(shortname: string): notoEmoji.Emoji | undefined {
        return notoEmoji.getEmojiData(this.config, shortname);
    }

    public emojiShortnameToUnicode(text: string) {
        const raw: string = this.unescapeQuotes(text);
        return notoEmoji.toParsedUnicode(this.config, raw);
    }

    private unescapeQuotes(string: string): string {
        const unescaped = { '&quot;': '"', '&#34;': '"', '&#x22;': '"', '&apos;': '\'', '&#39;': '\'', '&#x27;': '\'' };
        return string.replace(/&(?:apos|#39|#x27|quot|#34|#x22);/ig, (match) => { return unescaped[match]; });
    }

    public toParsedUnicode(text: string, config?: EmojiConfig): string {
        return notoEmoji.toParsedUnicode(config ?? this.config, text);
    }

    public getFlags(config?: EmojiConfig): Map<string, any> {
        return notoEmoji.getFlags(config ?? this.config);
    }

    public getEmojisData(text: string): EmojiData[] {
        const unicodes: string[] = [];

        text.replace(UNICODE_EMOJI_REGEX, (unicode) => { unicodes.push(unicode); return ''; });

        return unicodes.map((unicode) => {
            // eslint-disable-next-line @typescript-eslint/no-misused-spread
            const code = [...unicode].map(cd => cd.codePointAt(0)?.toString(16)).filter(Boolean).join(`_`);
            const title = this.config.unicodeMap.get(unicode) || '';
            return { unicode, code, title };
        });
    }
}
